import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { Button, Col, DatePicker, Divider, Form, Row, Select, Table } from 'antd';
import Input from 'antd/es/input/Input';
import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import { useApp } from '@/App';
import {
	ActivityRoboticSearchParam,
	useActivityRoboticSearch,
	useActivitySearchOptions,
} from '@/libs/fleet-api/services/robotic';
import { useForm } from '@/libs/antd/form';
import RoboticStatusBadge from '@/pages/robotic/components/RoboticStatusBadge';
import DurationTimer from '@/pages/robotic/components/DurationTimer';

const ActivityRoboticPraPencairanListPage: FC = () => {
	const { navigate } = useApp();

	const [searchParam, setSearchParam] = useState<ActivityRoboticSearchParam>({
		pagination: { pageNumber: 1, pageSize: 10 },
	});

	const searchQuery = useActivityRoboticSearch(searchParam);
	const optionsQuery = useActivitySearchOptions();
	const isLoading = searchQuery.isFetching;

	const dataSource = (searchQuery.data?.data?.items ?? []).map((item, index) => ({
		...item,
		key: index + 1,
	}));

	const statusOptions = (optionsQuery.data?.data?.statusOptions ?? []).map((o) => ({
		label: o.value ?? '',
		value: o.id ?? '',
	}));

	const searchByOptions = (optionsQuery.data?.data?.searchByOptions ?? []).map((o) => ({
		label: o.value ?? '',
		value: o.id ?? '',
	}));

	const { form, fieldName } = useForm<{
		dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
		status?: string;
		searchBy?: string;
		keyword?: string;
	}>();

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<FleetTitle>Activity Robotic Pra Pencairan</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ flex: 'none', style: { width: 150 } }}
				style={{ overflow: 'visible' }}
				onFinish={(values) => {
					const dateRange = values.dateRange;
					setSearchParam({
						pagination: { pageNumber: 1, pageSize: 10 },
						dateFrom:
							dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
						dateTo:
							dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
						status: values.status || undefined,
						search:
							values.keyword && values.searchBy
								? { by: values.searchBy, value: values.keyword }
								: undefined,
					});
				}}
			>
				<Row gutter={20}>
					<Col span={12}>
						<Form.Item name={fieldName('dateRange')} label="Tanggal Activity">
							<DatePicker.RangePicker
								style={{ width: '100%' }}
								format="DD/MM/YYYY"
								disabled={isLoading}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '20px 0' }} />
				<Row gutter={20}>
					<Col span={24}>
						<Form.Item
							name={fieldName('status')}
							initialValue=""
							label="Status"
						>
							<Select
								options={statusOptions}
								style={{ width: 160 }}
								disabled={isLoading}
								loading={optionsQuery.isLoading}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '20px 0' }} />
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchBy')}
							initialValue={searchByOptions[0]?.value}
							label="Kata Kunci"
						>
							<Select
								options={searchByOptions}
								style={{ width: 200 }}
								disabled={isLoading}
								loading={optionsQuery.isLoading}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item name={fieldName('keyword')}>
							<Input placeholder="Search" disabled={isLoading} />
						</Form.Item>
					</Col>
					<Col flex="none" style={{ width: 190 }}>
						<Button block type="primary" htmlType="submit" disabled={isLoading}>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider style={{ margin: 0 }} />

			<Table
				pagination={{
					total: searchQuery.data?.data.totalCount,
					pageSize: searchParam.pagination?.pageSize ?? 10,
					current: searchParam.pagination?.pageNumber ?? 1,
					onChange: (pageNumber) => {
						setSearchParam((prev) => ({
							...prev,
							pagination: { pageNumber, pageSize: prev.pagination.pageSize },
						}));
					},
				}}
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={dataSource}
				columns={[
					{
						title: 'Nama Customer',
						render: (_, record) => record.customerName || '-',
					},
					{
						title: 'FRM',
						render: (_, record) => record.frmName || '-',
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdhName || '-',
					},
					{
						title: 'No DKRPP',
						render: (_, record) => record.disbursementNo || '-',
					},
					{
						title: 'No PO',
						render: (_, record) => record.purchaseOrderNo || '-',
					},
					{
						title: 'Jenis Fasilitas',
						render: (_, record) => record.facilityType || '-',
					},
					{
						title: 'Asset Description',
						render: (_, record) => record.assetDescription || '-',
					},
					{
						title: 'Last Modified',
						render: (_, record) =>
							record.lastModified
								? dayjs(record.lastModified).format('DD/MM/YYYY HH:mm')
								: '-',
					},
					{
						title: 'Action',
						align: 'center',
						render: (_, record) => {
							const goToDetail = (e: React.MouseEvent) => {
								e.preventDefault();
								navigate('ActivityRoboticDetailPage' as never, {
									purchaseOrderNo: record.purchaseOrderNo,
									status: record.status,
								} as never);
							};
							if (record.isActionAllowed && (record.status === 'Waiting' || record.status === 'Pending')) {
								return <a onClick={goToDetail}>Feedback</a>;
							}
							if (record.isViewAllowed && (record.status === 'In Progress' || record.status === 'Finish')) {
								return <a onClick={goToDetail}>View Detail</a>;
							}
							return '-';
						},
					},
					{
						title: 'Status',
						render: (_, record) =>
							record.status ? (
								<RoboticStatusBadge status={record.status} />
							) : (
								'-'
							),
					},
					{
						title: 'Duration Status',
						render: (_, record) =>
							record.duration ? (
								<DurationTimer value={record.duration} />
							) : (
								'-'
							),
					},
				]}
			/>
		</div>
	);
};

export default ActivityRoboticPraPencairanListPage;
